/**
 * @name Accident.js
 * @author 양진영
 * @since 2025. 02. 26.
 * @description
 * 중대재해발생형태 관리
 */
import React, { useState, useEffect, useContext } from "react";
import { FormProvider } from "react-hook-form";
import { Select, Pagination, SortHeader } from "@components";
import { useAccidentList } from "@hooks/index";
import { post } from "@utils/axios_api";
import { toast } from 'react-toastify';
import { AuthContext } from "@context";

const Accident = () => {
  const { hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission('ACCIDENT_WRITE');

  const {
    methods, data, refetch, onSubmit,
    handlePageChange, changePageSize, initialAccident,
    pageIndex, recordCountPerPage,
  } = useAccidentList();

  const [selectAccident, setSelectAccident] = useState(initialAccident);

  useEffect(() => {
    refetch();
  }, [pageIndex, refetch])

  const handleAccident = (saccCode) => {
    const filter = data?.resultList.filter((item) => saccCode === item.saccCode);

    if (filter.length > 0) {
      setSelectAccident({ ...filter[0], isNew: false, isEdited: true })
    }

  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectAccident((prev) => ({
      ...prev,
      [name]: value,
      isNew: prev.isNew,
      isEdited: prev.isEdited,
    }));
  }

  const handleAdd = () => {
    setSelectAccident(initialAccident);
  }

  const handleSave = async () => {
    if (!selectAccident.categoryCode || !selectAccident.categoryName
      || !selectAccident.saccCode || !selectAccident.saccName) {
      toast.warn("필수 입력사항을 입력하십시오.");
      return;
    }

    if (selectAccident.categoryCode.length > 2) {
      toast.warn("구분코드는 2자 이내로 입력하십시오.");
      return;
    }
    if (selectAccident.categoryName.length > 500) {
      toast.warn("구분명칭은 500자 이내로 입력하십시오.");
      return;
    }
    if (selectAccident.saccCode.length > 4) {
      toast.warn("발생형태코드는 4자 이내로 입력하십시오.");
      return;
    }
    if (selectAccident.saccName.length > 500) {
      toast.warn("발생형태명은 500자 이내로 입력하십시오.");
      return;
    }
    if (selectAccident.notice && selectAccident.notice.length > 2000) {
      toast.warn("비고는 2000자 이내로 입력하십시오.");
      return;
    }

    if (!selectAccident.isNew && !selectAccident.isEdited) {
      toast.warn("저장할 사항이 없습니다.");
      return;
    }

    if (!window.confirm("저장 하시겠습니까?")) return;

    try {

      const saccCode = selectAccident.saccCode;
      const url = selectAccident.isNew ? "/admin/comm/accident/register" : `/admin/comm/accident/${saccCode}`;
      const response = await post(url, selectAccident, {
        headers: {
          headers: { 'Content-type': 'application/json' },
        }
      })

      toast.success(response.message);
      handlePageChange(pageIndex);
      refetch();

    } catch (error) {
      console.error("저장 중 오류:", error);
    }
  }


  const comboList = data?.comboList || [];
  const comboName = comboList.map((item) => ({ ...item, comboLabel: `${item.categoryCode} : ${item.categoryName}`, }))

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="contents-wrap">
          <div className="page-title-wrap">
            <h2><span className="icon-dot-circle"></span>중대재해 발생형태 관리</h2>
          </div>


          <div className="tab-cont-wrap">
            <div className="tab-cont-01 on">
              <div className="search-wrap">
                <div className="search-row">
                  <div className="search-item date-picker-box">
                    <label>구분코드</label>
                    <Select
                      name="searchWrd"
                      className="input-search"
                      options={comboName}
                      style={{ width: "300px", maxWidth: "300px" }}
                      defaultOption="선택"
                      valueKey="categoryCode"
                      labelKey="comboLabel"
                    />
                  </div>

                  <div className="search-btn-container">
                    <button className="btn-search" >조회</button>
                  </div>
                </div>
              </div>

              <div className="row-1">
                <section className="wp75">
                  <div className="section-tit-wrap">
                    <h3 className="border-left-primary">
                      &nbsp;중대재해 발생형태 목록 <span className="total-txt">
                        {" "} | 총 <span className="red">{data?.totalCount || 0}</span> 건
                      </span>
                    </h3>
                  </div>
                  <div className="table-container table-list-wrapper">
                    <table className="table-list">
                      <colgroup>
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "30%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "35%" }} />
                        <col style={{ width: "10%" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>순번</th>
                          <SortHeader field="DIVCODE" label="구분코드" />
                          <SortHeader field="DIVNAME" label="구분명칭" />
                          <th>발생형태코드</th>
                          <th>발생형태명</th>
                          <th>비고</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.resultList && data.resultList.length > 0 ? (
                          data.resultList.map((item, idx) => (
                            <tr
                              key={item.saccCode}
                              onClick={() => handleAccident(item.saccCode)}
                              style={{ cursor: "pointer" }}>
                              <td>{(pageIndex - 1) * recordCountPerPage + idx + 1}</td>
                              <td>{item.categoryCode}</td>
                              <td style={{ textAlign: "left" }}>{item.categoryName}</td>
                              <td>{item.saccCode}</td>
                              <td style={{ textAlign: "left" }}>{item.saccName}</td>
                              <td>{item.notice}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="table-empty" style={{ textAlign: 'center' }}>
                              조회 결과가 없습니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={recordCountPerPage}
                      onChange={(e) => changePageSize(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
                    <Pagination
                      totalPages={Math.ceil(
                        data.paginationInfo.totalRecordCount / data.paginationInfo.recordCountPerPage
                      )}
                      currentPage={pageIndex}
                      onPageChange={handlePageChange}
                      pageRangeDisplayed={10}
                    />
                  )}
                </section>
                <section className="wp25">

                  <div className="section-tit-wrap">
                    <h3 className='border-left-primary'>&nbsp;중대재해 발생형태 등록</h3>
                    <div className="btn-right-box">
                      <ul>
                        {canWrite && <li><button type="submit" title="추가" className="btn-save" onClick={handleAdd}>추가</button></li>}
                        {canWrite && <li><button type="submit" title="저장" className="btn-save" onClick={handleSave}>저장</button></li>}
                      </ul>
                    </div>
                  </div>

                  <table className="table-write">
                    <colgroup>
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '70%' }} />
                    </colgroup>
                    <tbody id="tblBody1">
                      <tr>
                        <th>구분코드<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <input
                            name="categoryCode"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectAccident.categoryCode || ""}
                            disabled={selectAccident.isEdited}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>구분명칭<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <input
                            name="categoryName"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectAccident.categoryName || ""}
                            disabled={selectAccident.isEdited}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>발생형태코드<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <input
                            name="saccCode"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectAccident.saccCode || ""}
                            disabled={selectAccident.isEdited}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>발생형태명<span style={{ color: "red" }}>*</span></th>
                        <td>
                          <input
                            name="saccName"
                            className="input-search"
                            style={{ width: '100%', maxWidth: 'none' }}
                            value={selectAccident.saccName || ""}
                            disabled={selectAccident.isEdited}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th>비고</th>
                        <td>
                          <textarea
                            name="notice"
                            style={{
                              width: '100%', height: '100px', resize: 'none',
                              wordWrap: 'break-word', whiteSpace: 'pre-wrap', borderColor: "#CCC"
                            }}
                            value={selectAccident.notice || ""}
                            onChange={handleChange}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};


export { Accident };
